import React from 'react';
import './ErrorBoundary.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Tell the server (POST /api/ops/client-error, routes/ops.js). Settings ->
 * Overview lists it beside the server's own errors, so a page that goes
 * white for one person is seen by whoever fixes things rather than described
 * over the phone. Best effort: a crash report must never crash anything, and
 * the server rate-limits and clips what it is sent. window.fetch is the
 * patched one (lib/apiClient.js), so the token goes with it when there is one.
 */
export function reportCrash(error, info) {
  try {
    const component = info && info.componentStack ? String(info.componentStack).slice(0, 2000) : '';
    const body = JSON.stringify({
      code: 'render',
      message: String((error && error.message) || error || 'Jarvis stopped rendering').slice(0, 500),
      stack: String((error && error.stack) || '').slice(0, 4000) + (component ? '--component--' + component : ''),
      url: window.location.pathname + window.location.search
    });
    return fetch(`${API_BASE}/ops/client-error`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true
    }).catch(() => {});
  } catch {
    return Promise.resolve();
  }
}

/**
 * Catches a render-time crash so it does not become a blank page.
 *
 * React unmounts the whole tree when a component throws during render, and
 * what is left is an empty <body>: no message, no navigation, nothing to
 * report but "it stopped working". That has happened here for real - a route
 * answered with an array where an object was expected, a state setter was
 * handed Array.prototype.entries, React called it as an updater, and the
 * portal went white.
 *
 * This does not fix anything. It turns a blank page into a sentence, a way
 * back, and something a contractor can read out over the phone.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Still worth having in the console for whoever is looking at the tab.
    console.error('Jarvis stopped rendering:', error, info?.componentStack);
    reportCrash(error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="crash" role="alert">
        <h1>Jarvis has stopped on this page</h1>
        <p>
          Nothing you did is lost &mdash; this is the page failing to draw, not the
          work behind it. Reloading usually gets you moving again.
        </p>
        <div className="crash-actions">
          <button type="button" className="crash-btn is-primary"
                  onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <button type="button" className="crash-btn"
                  onClick={() => window.location.assign('/')}>
            Go back to the start
          </button>
        </div>
        <p className="crash-small">
          If it keeps happening, this is the bit that helps whoever fixes it:
        </p>
        <pre className="crash-detail">{String(this.state.error?.message || this.state.error)}</pre>
      </div>
    );
  }
}
